import { getPickupMinLeadErrorMessage } from "./bookingLeadTime";

const MIN_GAP_AFTER_ONWARD_MS = 1 * 60 * 1000;
export const MAX_RETURN_WINDOW_MS = 12 * 60 * 60 * 1000;
export const DEFAULT_RETURN_OFFSET_MS = 6 * 60 * 60 * 1000;

export const OUTSTATION_ROUND_TRIP_RETURN_NOTE =
  "Note: Return pick-up must be within 12 hours of the onward pick-up time. For a later return, please make a separate booking.";

const SCHEDULE_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function parseBookingScheduleDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const s = String(value).trim();
  const direct = new Date(s);
  if (!Number.isNaN(direct.getTime())) return direct;

  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})/);
  if (m) {
    const d = new Date(
      Number(m[1], 10),
      Number(m[2], 10) - 1,
      Number(m[3], 10),
      Number(m[4], 10),
      Number(m[5], 10),
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }

  return null;
}

function toLocalDateTimeValue(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:00`;
}

export function formatScheduleLikeDatePicker(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  let hours = date.getHours();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hoursStr = hours.toString().padStart(2, "0");
  const minStr = date.getMinutes().toString().padStart(2, "0");
  return `${SCHEDULE_MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}, ${hoursStr}:${minStr} ${ampm}`;
}

export function getDefaultReturnPickupDate(onwardStr) {
  const onward = parseBookingScheduleDate(onwardStr);
  if (!onward) return null;
  return new Date(onward.getTime() + DEFAULT_RETURN_OFFSET_MS);
}

export function getDefaultReturnPickupDisplay(onwardStr) {
  const d = getDefaultReturnPickupDate(onwardStr);
  return d ? formatScheduleLikeDatePicker(d) : "";
}

/** DateSelectModal: return pick-up after onward, default +6h, max +12h. */
export function getReturnPickupWithin12hConfig(pickupTimeStr) {
  if (!pickupTimeStr) return {};
  const start = parseBookingScheduleDate(pickupTimeStr);
  if (!start) return {};
  const defaultReturn = new Date(start.getTime() + DEFAULT_RETURN_OFFSET_MS);
  const end = new Date(start.getTime() + MAX_RETURN_WINDOW_MS);
  return {
    minDateStr: toLocalDateTimeValue(start),
    maxDateStr: toLocalDateTimeValue(end),
    defaultDateStr: toLocalDateTimeValue(defaultReturn),
    noteMessage: OUTSTATION_ROUND_TRIP_RETURN_NOTE,
    minErrorMessage:
      "Return pick-up must be after the onward pick-up (minimum 1 minute gap).",
    maxErrorMessage:
      "Return pick-up must be within 12 hours of the onward pick-up time.",
  };
}

function rideScheduleDate(ride) {
  if (!ride?.ride_date || !ride?.pickup_time) return null;
  const time = String(ride.pickup_time).slice(0, 5);
  return parseBookingScheduleDate(`${ride.ride_date}T${time}:00`);
}

export function validateOutstationRoundTripReturnWindow(onwardStr, returnStr) {
  if (returnStr == null || String(returnStr).trim() === "") {
    return { ok: false, message: "Please select return pick-up time." };
  }

  const onward = parseBookingScheduleDate(onwardStr);
  const ret = parseBookingScheduleDate(returnStr);

  if (!onward || !ret) {
    return {
      ok: false,
      message: "Please select valid onward and return times.",
    };
  }

  const onwardMs = onward.getTime();
  const returnMs = ret.getTime();

  if (returnMs <= onwardMs || returnMs < onwardMs + MIN_GAP_AFTER_ONWARD_MS) {
    return {
      ok: false,
      message:
        "Return pick-up must be after the onward pick-up (minimum 1 minute gap).",
    };
  }

  if (returnMs > onwardMs + MAX_RETURN_WINDOW_MS) {
    return {
      ok: false,
      message:
        "Return pick-up must be within 12 hours of the onward pick-up time.",
    };
  }

  return { ok: true };
}

/**
 * DateSelectModal config for rescheduling a ride in a round-trip booking.
 * Return ride: after onward (+1 min), and within 12h for outstation.
 * Onward ride: before return (−1 min), and after now (via DateSelectModal lead).
 */
export function getRescheduleDatePickerConfig(ride, booking) {
  const rides = Array.isArray(booking?.rides_data)
    ? booking.rides_data
    : Array.isArray(booking?.rides)
      ? booking.rides
      : [];
  if (!ride || rides.length < 2) return {};

  const sorted = [...rides].sort((a, b) => Number(a.id) - Number(b.id));
  const onward = sorted[0];
  const returnRide = sorted[sorted.length - 1];
  const isReturn = Number(ride.id) === Number(returnRide?.id);
  const isOutstation = Number(booking?.ride_type) === 3;
  const config = {};

  if (isReturn && onward) {
    const onwardDt = rideScheduleDate(onward);
    if (!onwardDt) return {};
    config.minDateStr = toLocalDateTimeValue(onwardDt);
    config.minErrorMessage =
      "Return pick-up must be after the onward pick-up (minimum 1 minute gap).";
    if (isOutstation) {
      config.maxDateStr = toLocalDateTimeValue(
        new Date(onwardDt.getTime() + MAX_RETURN_WINDOW_MS),
      );
      config.defaultDateStr = toLocalDateTimeValue(
        new Date(onwardDt.getTime() + DEFAULT_RETURN_OFFSET_MS),
      );
      config.noteMessage = OUTSTATION_ROUND_TRIP_RETURN_NOTE;
      config.maxErrorMessage =
        "Return pick-up must be within 12 hours of the onward pick-up time.";
    }
    return config;
  }

  if (!isReturn && returnRide) {
    const returnDt = rideScheduleDate(returnRide);
    if (!returnDt) return {};
    config.maxDateStr = toLocalDateTimeValue(
      new Date(returnDt.getTime() - MIN_GAP_AFTER_ONWARD_MS),
    );
    config.maxErrorMessage =
      "Pick-up must be at least 1 minute before the return pick-up time.";
    config.minErrorMessage =
      getPickupMinLeadErrorMessage();
  }

  return config;
}

export function validateRescheduleAgainstSiblingRides(
  ride,
  booking,
  selectedScheduleStr,
) {
  const selected = parseBookingScheduleDate(selectedScheduleStr);
  if (!selected) {
    return { ok: false, message: "Please select a valid schedule." };
  }

  const config = getRescheduleDatePickerConfig(ride, booking);
  if (config.minDateStr) {
    const min = parseBookingScheduleDate(config.minDateStr);
    if (min && selected.getTime() < min.getTime() + MIN_GAP_AFTER_ONWARD_MS) {
      return {
        ok: false,
        message:
          config.minErrorMessage ||
          "Selected time must be after the related ride (minimum 1 minute gap).",
      };
    }
  }
  if (config.maxDateStr) {
    const max = parseBookingScheduleDate(config.maxDateStr);
    if (max && selected.getTime() > max.getTime()) {
      return {
        ok: false,
        message:
          config.maxErrorMessage ||
          "Selected time is outside the allowed window.",
      };
    }
  }

  return { ok: true };
}
